import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Calendar, Clock, Award, CalendarDays, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const companyHolidays = [
  { name: 'Republic Day', date: 'Monday, January 26, 2026' },
  { name: 'Holi (Half Day and WFH)', date: 'Monday, March 9, 2026' },
  { name: 'Gudi Padwa (Half Day and WFH)', date: 'Thursday, April 2, 2026' },
  { name: 'Maharashtra Day / Labour Day', date: 'Friday, May 1, 2026' },
  { name: 'Independence Day', date: 'Saturday, August 15, 2026' },
];

export default function EmployeeDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState(user);
  const [stats, setStats] = useState({
    totalLeavesTaken: 0,
    pendingRequests: 0,
    compOffEarned: 0,
    nextLeave: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [leavesRes, userRes] = await Promise.all([
        axios.get(`${API}/leaves`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      const allLeaves = leavesRes.data;
      const upcomingApproved = allLeaves.filter(l => l.status === 'Approved' && new Date(l.end_date) >= new Date());
      
      // Calculate stats
      const currentYear = new Date().getFullYear();
      const totalTaken = allLeaves
        .filter(l => l.status === 'Approved' && new Date(l.created_at).getFullYear() === currentYear)
        .reduce((sum, l) => sum + l.days_off, 0);
      
      const pending = allLeaves.filter(l => l.status === 'Pending').length;
      
      const nextLeave = upcomingApproved.length > 0 
        ? upcomingApproved.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0]
        : null;
      
      setStats({
        totalLeavesTaken: totalTaken,
        pendingRequests: pending,
        compOffEarned: userRes.data.comp_off_balance,
        nextLeave,
      });
      
      setLeaves(upcomingApproved);
      setBalances(userRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Approved') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Welcome back, {user.name.split(' ')[0]}!</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">{formatDate()}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-3 md:px-4 py-1.5 md:py-2 bg-muted rounded-lg text-xs md:text-sm font-medium text-foreground">
                {user.role}
              </div>
              <button className="relative p-2 hover:bg-muted rounded-lg transition-colors">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="total-leaves-taken">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Leaves Taken</p>
              <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground">{stats.totalLeavesTaken}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">This year</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="pending-requests">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Pending</p>
              <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground">{stats.pendingRequests}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">Awaiting approval</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="comp-off-earned">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Comp Off</p>
              <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground">{stats.compOffEarned}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">Available to use</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="leaves-left">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Leaves Left</p>
              <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground">{balances.paid_leave_balance || 0}</h3>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">Paid leaves available</p>
            </div>
          </div>

          {/* Upcoming Leaves & Company Holidays */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Upcoming Leaves */}
            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="upcoming-leaves">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">Upcoming Leaves</h3>
                <button 
                  onClick={() => navigate('/leave-history')}
                  className="text-xs md:text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  View All
                </button>
              </div>
              
              {leaves.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <Calendar className="w-12 h-12 md:w-16 md:h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-sm text-muted-foreground">No upcoming leaves</p>
                </div>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {leaves.slice(0, 5).map((leave) => (
                    <div key={leave.leave_id} className="flex items-center justify-between p-3 md:p-4 rounded-lg bg-muted hover:bg-muted/70 transition-colors" data-testid={`leave-item-${leave.leave_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-medium text-xs md:text-sm text-foreground truncate">{leave.leave_type}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`status-badge border text-xs ml-2 ${getStatusBadgeClass(leave.status)}`} data-testid={`leave-status-${leave.leave_id}`}>
                        {leave.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Company Holidays */}
            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="company-holidays">
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">Company Holidays</h3>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                {companyHolidays.map((holiday, index) => (
                  <div key={index} className="p-3 md:p-4 rounded-lg bg-muted" data-testid={`holiday-${index}`}>
                    <p className="font-heading font-medium text-xs md:text-sm text-foreground">{holiday.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{holiday.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
